import { type ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { useAuth } from "@/features/auth/AuthProvider";
import { useIsAdmin } from "@/features/auth/useIsAdmin";

/**
 * SPEC-22 — admin-only route guard.
 *
 * Like RequireAuth, but additionally requires `isAdmin`. Renders a
 * blank `bg-background` splash while either auth or admin detection is
 * still resolving (prevents a flash of "/" for an authed admin on
 * reload). Unauthed users bounce to /login; authed-but-not-admin users
 * bounce to /.
 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsAdmin();

  if (authLoading || adminLoading) {
    return <div className="min-h-screen w-full bg-background" aria-hidden />;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
